import { useContext } from "react";
import { useSelector } from "react-redux";
import dynamic from "next/dynamic";
import classNames from "classnames";

import { ExternalButton } from "@components/shared/buttons";
import CartCheckout from "@components/store/cart/cart-checkout";
import CartCheckoutCta from "@components/store/cart/cart-checkout-cta";
import PricelessShopCartContent from "@components/store/cart/priceless-shop-cart-content";
import CartFooter from "@sharedComponents/cart-footer";
import Loaders from "@stadium/ui/src/components/loaders/loaders";
import CartListing from "@sharedComponents/cart/cart-listing";
import { EmptyBag } from "@sharedComponents/cart/common";
import CartListingForLocker from "@sharedComponents/cart/cart-listing-locker";
import MinimalFooter from "@stadium/ui/src/components/footer";
import SwagCartNote from "@components/shared/cart/swag-cart-note";
import Portal from "@stadium/ui/src/components/portal";
import { CartContinueShoppingCta } from "@components/cart/wrapper";

import { getMenuRoute } from "@constants/routes";
import { getIsLockerShop, getIsPricelessShop, getStoreCurrencyDetails } from "@selectors/store";
import Wallet from "@stadium/checkout/src/components/payment-options/wallet";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useStoreContext } from "../provider";

import { useCheckoutContext } from "@contexts/checkout-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import {
  getCartItemsCount,
  getCartOrderFetching,
  getOrderTeamWalletId,
  getOrderUseGlobalPoint,
  getOrderUseWalletMoney,
} from "@selectors/cart";
import { getStoreIsOrderFetching } from "@store/selectors/store";
import { useCheckoutContext as useGlobalCheckoutContext } from "@stadium/checkout/src/contexts/checkout";

import { ASSET_MISSING_NOTE, PURCHASING_SWAG_NOTE } from "@constants/orders/cart";
import { CONTINUE_SHOPPING_TARGET } from "@constants/analytics/cart";

import styles from "./main.module.scss";

const RequestMockupConfirmationModal = dynamic(() =>
  import("@components/modals/request-mockup/confirmation"),
);

const Cart = ({ cmsSectionIndex = 0 }) => {
  const editable = true;

  const { storeBasePath, isNewSwagmagicHost } = useStoreContext();
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const isLockerShop = useSelector(getIsLockerShop);
  const useWalletMoney = useSelector(getOrderUseWalletMoney);
  const useGlobalPoint = useSelector(getOrderUseGlobalPoint);
  const storeDetails = useSelector(getStoreCurrencyDetails);
  const defaultTeamWalletId = useSelector(getOrderTeamWalletId);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const itemsCount = useSelector(getCartItemsCount);
  const orderFetching = useSelector(getCartOrderFetching);
  const isStoreOrderFetching = useSelector(getStoreIsOrderFetching);

  const { previewMode, isGuestOrPreviewMode } = usePreviewModeContext();

  const {
    cartItemsQuantity,
    teamWallets,
    getCartTotalInSelectedCurrency,
    cartTotal,
    isRequestMockupConfirmationModalOpen,
    closeRequestMockupConfirmationModal,
    isAnyItemWithoutDigitalAsset,
  } = useCheckoutContext();
  const { currency } = useGlobalCheckoutContext();

  const isCartPageLoading =
    (isLockerShop || isPricelessShop) &&
    ((isStoreOrderFetching && !isGuestOrPreviewMode) || orderFetching);

  const isLockerCartEmpty = isLockerShop && itemsCount === 0;

  const cartTotalInSelectedCurrency = getCartTotalInSelectedCurrency({
    totalToConvert: cartTotal,
  });

  const renderContinueShoppingCta = () => (
    <ExternalButton
      href={getMenuRoute({ storeBasePath })}
      styleType="link"
      className={styles.continueLink}
      eventLocationAnalytics={{ track: true, target: CONTINUE_SHOPPING_TARGET }}
    >
      <i className={styles.iconArrowLeft} />
      {isMobileDevice ? "BACK" : <span>Continue SHOPPING</span>}
    </ExternalButton>
  );

  const renderCartCheckoutCta = () => {
    return <CartCheckoutCta />;
  };

  const renderFooter = () => {
    if (!isMobileDevice) {
      return <MinimalFooter />;
    }
    return (
      <CartFooter
        backCta={renderContinueShoppingCta}
        nextCta={renderCartCheckoutCta}
        className={previewMode ? styles.previewModeFooter : ""}
        footerMessageContent={
          isAnyItemWithoutDigitalAsset && (
            <div className={classNames(styles.assetMissingText, styles.assetMissingTextPadding)}>
              {ASSET_MISSING_NOTE}
            </div>
          )
        }
      />
    );
  };

  const renderCartListing = () =>
    isLockerShop ? (
      <CartListingForLocker />
    ) : (
      <CartListing editable={editable} fetchingOrder={false} />
    );

  const renderRightBlock = () =>
    isPricelessShop ? <PricelessShopCartContent /> : <CartCheckout editable={editable} />;

  const renderCartContent = () => (
    <>
      <div
        className={classNames(styles.leftBlock, {
          [styles.leftBlockExtraPadding]: isMobileDevice && isAnyItemWithoutDigitalAsset,
        })}
      >
        <div
          className={classNames(styles.bagHeading, {
            [styles.bagHeadingV2]: isNewSwagmagicHost,
          })}
        >
          My Bag {cartItemsQuantity ? `(${cartItemsQuantity})` : null}
        </div>
        {isMobileDevice && renderRightBlock()}
        {cartItemsQuantity && !isPricelessShop ? (
          <Wallet
            teamWallets={teamWallets}
            orderTotal={cartTotalInSelectedCurrency}
            billingCurrencyCode={currency?.code}
            defaultValues={{
              defaultUseWallet: useWalletMoney,
              defaultUseStadiumPoints: useGlobalPoint,
              defaultTeamWalletId,
            }}
            storeDetails={storeDetails}
            eventLocationAnalytics={{
              track: true,
            }}
          />
        ) : null}
        {renderCartListing()}
        {isMobileDevice && (
          <SwagCartNote noteText={PURCHASING_SWAG_NOTE} className={styles.swagPurchaseNote} />
        )}
      </div>
      {!isMobileDevice && <div className={styles.rightBlock}>{renderRightBlock()}</div>}
    </>
  );

  const renderSectionBody = () => {
    if (isCartPageLoading) {
      return (
        <div>
          <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-100" />
        </div>
      );
    }

    if (isLockerCartEmpty) {
      return (
        <EmptyBag menuPath={getMenuRoute({ storeBasePath })} className={styles.onlyEmptySection} />
      );
    }

    return (
      <div
        className={classNames(
          styles.myBagContainer,
          {
            [styles.isPricelessShop]: isPricelessShop,
            [styles.extraMargin]: previewMode,
            [styles.midCmsSectionContainer]: cmsSectionIndex !== 0,
            [styles.myBagContainerV2]: isNewSwagmagicHost,
          },
          styles.withCheckout,
        )}
      >
        <div
          className={`${styles.rowBlockgroup} ${cmsSectionIndex === 0 ? "" : styles.midCmsSection}`}
        >
          {renderCartContent()}
        </div>
      </div>
    );
  };

  const isLockerEmptyLayout = isLockerCartEmpty && !isCartPageLoading;

  return (
    <>
      <Portal
        id="cart-back-cta-container"
        dataContainerId="cart-back-cta-portal"
        className={styles.backCtaContainer}
      >
        {!isMobileDevice && <CartContinueShoppingCta />}
      </Portal>
      <section
        className={classNames(
          styles.wrapper,
          {
            [styles.emptyLockerCartWrapper]: isLockerEmptyLayout,
          },
          "wrapper",
        )}
      >
        {renderSectionBody()}
        {isRequestMockupConfirmationModalOpen && (
          <RequestMockupConfirmationModal closeModal={closeRequestMockupConfirmationModal} />
        )}
      </section>
      <Portal id="cart-footer-container" dataContainerId="cart-footer-portal">
        {renderFooter()}
      </Portal>
    </>
  );
};

export default Cart;
